import { useMemo, type FC } from 'react';
import clsx from 'clsx';

import { formatTime } from '../utils/formatTime';
import { TimeMarker } from './TimeMarker';

export interface VideoTimelineProps {
  duration: number;
  currentTime: number;
  startTime: number | null;
  endTime: number | null;
}

/**
 * Accessible timeline overlay for video with start/end time markers.
 * Shows current playback position, timestamp markers, and highlighted region.
 */
export const VideoTimeline: FC<VideoTimelineProps> = ({ duration, currentTime, startTime, endTime }) => {
  const hasSegment = startTime !== null || endTime !== null;
  const hasFullSegment = startTime !== null && endTime !== null;

  // Memoize percentage calculations
  const positions = useMemo(() => {
    const getPercentage = (time: number) => Math.min(100, Math.max(0, (time / duration) * 100));

    return {
      current: getPercentage(currentTime),
      start: startTime !== null ? getPercentage(startTime) : 0,
      end: endTime !== null ? getPercentage(endTime) : 0,
      segmentWidth: startTime !== null && endTime !== null ? getPercentage(endTime - startTime) : 0,
    };
  }, [currentTime, startTime, endTime, duration]);

  // Calculate if we're currently within the highlighted segment
  const isInSegment = useMemo(() => {
    if (!hasSegment) return false;
    const start = startTime ?? 0;
    const end = endTime ?? duration;

    return currentTime >= start && currentTime <= end;
  }, [currentTime, startTime, endTime, duration, hasSegment]);

  // Build accessible description
  const accessibleDescription = useMemo(() => {
    const parts: string[] = [`Current time: ${formatTime(currentTime)} of ${formatTime(duration)}`];

    if (startTime !== null) parts.push(`Start marker at ${formatTime(startTime)}`);
    if (endTime !== null) parts.push(`End marker at ${formatTime(endTime)}`);
    if (hasFullSegment) parts.push(`Highlighted segment from ${formatTime(startTime!)} to ${formatTime(endTime!)}`);

    return parts.join('. ');
  }, [currentTime, duration, startTime, endTime, hasFullSegment]);

  return (
    <div
      className="absolute bottom-0 left-2 right-2 pointer-events-none"
      role="region"
      aria-label="Video timeline with timestamp markers"
    >
      {/* Screen reader description */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {accessibleDescription}
      </div>

      {/* Timeline container with gradient backdrop for visibility */}
      <div className="relative rounded-lg px-3 py-1! -mx-3 -mb-3">
        {/* Segment indicator badge */}
        {hasSegment && (
          <div className="flex justify-center mb-2 text-xs font-medium text-white tabular-nums">
            <span
              className={clsx(
                'px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wide transition-colors duration-200',
                isInSegment ? 'bg-green-600' : 'bg-dark-200 text-dark-900!',
              )}
              aria-hidden="true"
            >
              {isInSegment ? '● In segment' : 'Segment'}
            </span>
          </div>
        )}

        {/* Progress track */}
        <div
          className="relative h-1.5 bg-dark-600 rounded-full overflow-visible"
          role="progressbar"
          aria-valuenow={Math.round(currentTime)}
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
        >
          {/* Played progress (dimmed outside segment) */}
          <div
            className="absolute top-0 left-0 h-full bg-white/40 rounded-full transition-[width] duration-100"
            style={{ width: `${positions.current}%` }}
            aria-hidden="true"
          />

          {/* Highlighted segment background */}
          {hasFullSegment && (
            <div
              className="absolute top-0 h-full bg-emerald-400/50 rounded-full"
              style={{ left: `${positions.start}%`, width: `${positions.segmentWidth}%` }}
              aria-hidden="true"
            />
          )}

          {/* Played progress within segment (brighter) */}
          {hasFullSegment && (
            <div
              className="absolute top-0 h-full bg-emerald-400 rounded-full transition-[width] duration-100"
              style={{
                left: `${positions.start}%`,
                width: `${Math.max(0, Math.min(positions.current - positions.start, positions.segmentWidth))}%`,
              }}
              aria-hidden="true"
            />
          )}

          {/* Time markers */}
          {startTime !== null && (
            <TimeMarker position={`clamp(1px, ${positions.start}%, calc(100% - 1px))`} time={startTime} />
          )}
          {endTime !== null && (
            <TimeMarker position={`clamp(1px, ${positions.end}%, calc(100% - 1px))`} time={endTime} />
          )}

          {/* Current time scrubber */}
          <div
            className={clsx(
              'absolute inset-y-0 -top-1! my-auto -translate-x-1/2 w-4 h-4 rounded-full shadow-lg shadow-black/30 transition-[left] duration-100',
              isInSegment ? 'bg-emerald-400 ring-2 ring-emerald-300/50' : 'bg-white ring-2 ring-white/30',
            )}
            style={{ left: `clamp(6px, ${positions.current}%, calc(100% - 6px))` }}
            aria-hidden="true"
          />
        </div>

        {/* Bottom padding for marker labels */}
        {hasSegment && <div className="h-4" aria-hidden="true" />}
      </div>
    </div>
  );
};
